import type { Theme as GlitzTheme } from '@glitz/core';
import { ThemeProvider as GlitzThemeProvider } from '@glitz/react';
import * as React from 'react';
import { a11yGrey, paleBlack, secondaryGrey, sharedFontSize, sharedSpacing } from 'webapp/shared/sharedTheme';

export const black = '#000000';
export const white = '#fff';
export const grey = '#E4E4E4'; //aka disabled color
export const primary = black;
export const primaryDark = '#25272c'; //used in footer

export const transactionally = '#47A078';
export const transactionallyLight = '#4EC791';

const secondaryColor = '#EDECE8';
export const secondaryDark = '#E3DFDC';
export const secondaryLight = '#F6F5F3';

export const imageBgColor = '#f8f7f6';

/*Surface colors */
export const monochrome = '#F1EFED';
export const monochromeDark = ' #C9C0BB';
export const monochromeDarker = '#88817D';

/*Alternative colors */
export const blue = '#CEE0E4';
export const pink = '#F1D7C9';
export const green = '#526456';
export const purple = '#7E4F5F';
export const brown = '#7A5B51';
export const darkBlue = '#093FFF';
export const lightBlue = '#B7C7FF';

/*Outlet & Campaign */
export const campaign = '#BB2B34';

/*Icon colors*/
export const lightGrey = '#FAFAFA';
export const mediumGrey = '#9F9F9F';

/*System colors */
export const yellow = '#EEA73C';
export const positive = transactionally;
export const positiveLight = transactionallyLight;
export const positiveDark = '#2E8A61';
export const negative = '#D90005'; //aka alert color, to be used on error message texts
export const negativeLight = '#F14F53';
export const negativeDark = '#C40005';

const secondaryBrighter = '#ffe0b3';
const secondaryBrightest = '#fff0d9';
const darkGrey = '#1e2022';
const monochromeLight = '#ebebeb';
const creamWhite = '#FFF1D9';

const news = '#8b7f79';
const warmRed = '#862E18';
const darkWarmRed = '#5F2010';

const secondary = {
  color: transactionally,
  hover: transactionallyLight,
  text: white,
  border: transactionally,
};

const primaryTheme = {
  color: primary,
  hover: black,
  text: white,
  border: primary,
};

const quaternaryTheme = {
  color: warmRed,
  hover: darkWarmRed,
  text: white,
  border: warmRed,
};

const monochromeTheme = {
  color: monochrome,
  hover: secondaryDark,
  text: primary,
  border: monochrome,
};

const whiteTheme = {
  color: white,
  hover: monochrome,
  text: black,
  border: monochromeDark,
  hoverBorder: monochromeDark,
};

const fontSize = { xxxl: 56, xxl: 40, xl: 32, l: 25, m: 20, s: 18, xs: 16, xxs: 14, xxxs: 12 };

const fontWeight = {
  bold: 500,
  semibold: 450,
  normal: 400,
  light: 300,
  primary: 500,
  secondary: 400,
};

export const theme: GlitzTheme = {
  id: 'svenssons',
  primary: primaryTheme,
  secondary,
  tertiary: {
    color: primary,
    hover: black,
    text: white,
    border: primary,
  },
  quaternary: quaternaryTheme,
  monochrome: monochromeTheme,
  monochromeBrighter: {
    color: monochrome,
    hover: monochromeDark,
    text: 'black',
    border: monochrome,
  },
  positive: {
    color: transactionally,
    hover: transactionallyLight,
    text: white,
    border: transactionally,
  },
  negative: {
    color: negative,
    hover: negativeDark,
    text: white,
    border: negative,
  },
  transparent: {
    color: 'transparent',
    hover: 'transparent',
    text: 'inherit',
    border: 'transparent',
  },
  white: whiteTheme,
  //Dark is same as primary
  dark: {
    color: primary,
    hover: black,
    text: white,
    border: primary,
  },
  disabled: {
    color: grey,
    hover: grey,
    text: '#9a9a9a',
    border: grey,
  },
  myPagesMenu: {
    color: monochromeDark,
    fontWeight: fontWeight.normal,
  },
  myPagesLoyaltyPoints: {
    color: monochrome,
    fontWeight: fontWeight.normal,
  },
  myPagesInfoBox: {
    color: monochrome,
  },
  myPagesProfilePage: {
    color: monochromeDark,
    text: monochromeDarker,
  },
  notificationColor: {
    color: darkBlue,
    text: white,
  },
  myPagesButton: {
    color: secondaryColor,
    fontWeight: fontWeight.light,
  },
  myPagesText: {
    fontWeight: fontWeight.semibold,
  },
  quickSearch: {
    borderColor: monochromeDark,
    backgroundColor: white,
    borderRadius: 0,
    highlightText: black,
  },
  searchSuggestions: {
    borderRadius: 0,
    position: 33,
    borderColor: secondaryGrey,
    color: a11yGrey,
    backgroundColor: white,
  },
  partialDeliveryTheme: {
    color: lightBlue,
  },
  productPageInfo: {
    color: monochrome,
  },
  fontFamily: {
    primary: 'charter',
    secondary: 'circular',
    tertiary: 'charter',
  },
  footer: {
    heading: {
      fontSize: fontSize.s,
      fontWeight: fontWeight.semibold,
    },
    color: warmRed,
    text: white,
  },
  headerIndicators: {
    cart: {
      backgroundColor: transactionally,
      color: white,
    },
    favourites: {
      backgroundColor: warmRed,
      color: white,
    },
  },
  carouselButton: {
    color: monochrome,
    hover: secondaryDark,
    text: black,
    border: monochrome,
  },
  secondaryCarouselButton: {
    color: white,
    hover: secondaryColor,
    text: black,
    border: white,
  },
  actionButton: secondary,
  filterButton: monochromeTheme,
  linkButton: primaryTheme,
  wishlistButton: whiteTheme,
  button: { textTransform: 'initial', fontWeight: fontWeight.normal },
  bubble: { color: white, backgroundColor: primary },
  sharedSpacing: sharedSpacing,
  spacing: { xxxxl: 80, xxxl: 64, xxl: 56, xl: 48, l: 40, m: 32, s: 24, xs: 16, xxs: 8 },
  sharedFontSize: sharedFontSize,
  fontSize,
  fontWeight,
  radius: {
    primary: 0,
    secondary: 0,
  },
  checkbox: {
    color: darkGrey,
    uncheckedColor: 'transparent',
    checkedColor: white,
    disabled: monochrome,
    checkedBackgroundColor: darkGrey,
    borderColor: darkGrey,
    borderRadius: 3,
  },
  monochromeLight,
  darkGrey,
  secondaryBrighter,
  secondaryBrightest,
  secondaryLight,
  creamWhite,
  lightBackground: monochrome,
  flyout: {
    headerColor: secondaryColor,
    headerHeight: 72,
    headerTextPosition: 'center',
    backgroundColor: secondaryColor,
  },
  borderColor: monochromeDark,
  borderHighlight: black,
  breadcrumbs: {
    fontSizeLargeViewport: fontSize.xxs,
    fontSizeBase: fontSize.xxs,
    segmentFontWeight: 'bold',
    lastSegmentColor: darkGrey,
    color: darkGrey,
  },
  borderStrongColor: monochromeDark,
  stockStatus: {
    inStock: positive,
    lowInStock: yellow,
    outOfStock: negative,
    unknown: primary,
  },
  productCard: {
    brand: { fontSize: fontSize.xxs, fontWeight: 700 },
    title: { fontSize: fontSize.xxs, color: black },
    price: { fontSize: fontSize.xxs },
    wishlistIcon: { color: black },
  },
  topBanner: {
    color: warmRed,
    text: white,
    checkColor: 'inherit',
  },
  newsBadge: {
    color: news,
    text: white,
  },
  thumbnailBorder: monochromeDark,
  header: {
    backgroundColor: monochrome,
    menuTextTransform: 'initial',
    fontSize: fontSize.xxs,
    fontWeight: fontWeight.bold,
    subCategoryFontSize: fontSize.xs,
    menuHeadingFontSize: fontSize.m,
    menuHightlightColor: monochromeDark,
    topMenu: { fontWeight: fontWeight.normal },
  },
  mobileMenu: {
    backgroundColor: white,
    accentColor: secondaryColor,
    borderColor: monochromeDark,
    fontSize: fontSize.xs,
    fontWeight: fontWeight.normal,
    tabs: {
      fontSize: fontSize.xs,
      fontWeight: fontWeight.normal,
      textTransform: 'uppercase',
      fontFamily: 'charter',
    },
  },
  facets: {
    flyoutHeader: { fontSize: fontSize.s, color: secondaryColor },
    heading: { fontSize: fontSize.xs, fontWeight: fontWeight.normal },
    facet: { fontSize: fontSize.xxs },
    count: { fontSize: fontSize.xxs, color: monochromeDarker },
    borderColor: monochromeDark,
    search: {
      color: white,
      borderColor: monochromeDark,
    },
    range: {
      sliderMarkColor: primary,
      sliderTrackColor: primary,
      sliderRailColor: secondaryDark,
      markDiameter: 16,
      railHeight: 4,
    },
    adornment: {
      input: { color: monochromeDarker },
      affix: { fontSize: sharedFontSize.xs, color: monochromeDarker },
    },
  },
  scrollbar: {
    trackColor: secondaryColor,
    thumbColor: monochromeDark,
    radius: 0,
  },
  imageBackgroundColor: 'rgba(128, 122, 106, 0.06)',
  focalPoint: {
    iconBackgroundColor: secondaryColor,
    imageBackgroundColor: imageBgColor,
  },
  outline: paleBlack,
  inputFocusOutlineColor: monochromeDarker,
  checkout: {
    backgroundColor: monochrome,
    text: primary,
  },
  materialSamples: {
    titleFontWeight: fontWeight.normal,
    descriptionFontWeight: fontWeight.normal,
    stockStatusFontWeight: fontWeight.normal,
  },
};

export function ThemeProvider(props: React.PropsWithChildren<{}>) {
  return <GlitzThemeProvider theme={theme}>{props.children}</GlitzThemeProvider>;
}

// Used for `@glitz/static`
export const staticThemes = [theme];
